import { proxy } from "valtio";

import type { SeatCell } from "@busbud/int-schemas/udm";

import { MAX_PASSENGERS_NUMBER } from "@app/constants/passengers";

export interface SegmentSelectedSeats {
  segment_id: string;
  seats: SeatCell[];
}

const SSF_OVERBOOKING = "SEAT_SELECTION_OVERBOOKING";
export const SSF_INVALID_SEATS = "SEAT_SELECTION_INVALID_SEATS";

interface SeatSelectionFirstError {
  type: typeof SSF_OVERBOOKING | typeof SSF_INVALID_SEATS;
  details?: Record<string, unknown>;
}
interface SeatSelectionFirstProxy {
  active_floor: number;
  selected_seats: SegmentSelectedSeats[];
  error: null | SeatSelectionFirstError;
}

function isSeatSelected(seat_id: string, segment_id: string) {
  const segment_idx = getSegmentSelectedSeatsIndex(segment_id);
  const selected_seats_for_segment =
    segment_idx !== -1 ? valtio_store.selected_seats[segment_idx] : null;

  if (!selected_seats_for_segment) {
    return false;
  }

  return selected_seats_for_segment.seats.some(s => s.id === seat_id);
}

export function isFirstSegment(segment_id: string) {
  return (
    valtio_store.selected_seats.length === 0 ||
    getSegmentSelectedSeatsIndex(segment_id) === 0
  );
}

function getSegmentSelectedSeatsIndex(segment_id: string) {
  return valtio_store.selected_seats.findIndex(
    s => s.segment_id === segment_id
  );
}

function removeSeat(seat_id: string, segment_id: string) {
  const segment_idx = getSegmentSelectedSeatsIndex(segment_id);

  // Remove seat_id from the specific selected_seats
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  valtio_store.selected_seats[segment_idx].seats = valtio_store.selected_seats[
    segment_idx
  ].seats.filter(s => s.id !== seat_id);

  // Remove empty selected seats
  valtio_store.selected_seats = valtio_store.selected_seats.filter(
    s => s.seats.length > 0
  );

  return true;
}

export function addSeat(seat: SeatCell, segment_id: string) {
  const segment_idx = getSegmentSelectedSeatsIndex(segment_id);
  const can_add_seat = canAddMoreSeats(segment_id);

  if (segment_idx === -1) {
    valtio_store.selected_seats.push({ segment_id, seats: [seat] });
    return true;
  } else if (can_add_seat) {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const is_seat_already_added = valtio_store.selected_seats[
      segment_idx
    ].seats.find(segment_seat => seat.id === segment_seat.id);
    if (is_seat_already_added) {
      return false;
    }

    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    valtio_store.selected_seats[segment_idx].seats.push(seat);
    return true;
  }

  return false;
}

export function getSegmentSelectedSeats(segment_id: string) {
  const idx = getSegmentSelectedSeatsIndex(segment_id);
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return idx !== -1 ? valtio_store.selected_seats[idx].seats : [];
}

export function getCountPendingSeatsBySegmentId(segment_id: string) {
  return getPassengersCount() - getSegmentSelectedSeats(segment_id).length;
}

export function getPassengersCount() {
  // We always use the first selected_seats segment as the reference
  return valtio_store.selected_seats[0]?.seats.length || 0;
}

function isSegmentSeatSelectionOverbooking(segment_id: string) {
  const passengers_count = getPassengersCount();
  const current_segment_selected_seats_count =
    getSegmentSelectedSeats(segment_id).length;

  return current_segment_selected_seats_count > passengers_count;
}

export function isSegmentSeatSelectionCompleted(segment_id: string) {
  const passengers_count = getPassengersCount();
  const current_segment_selected_seats_count =
    getSegmentSelectedSeats(segment_id).length;

  return (
    current_segment_selected_seats_count !== 0 &&
    passengers_count === current_segment_selected_seats_count
  );
}

export function canAddMoreSeats(segment_id: string) {
  const segment_idx = getSegmentSelectedSeatsIndex(segment_id);
  if (segment_idx < 0) {
    return true;
  }

  const is_first_segment = segment_idx === 0;
  const can_add_seat = is_first_segment
    ? getPassengersCount() < MAX_PASSENGERS_NUMBER
    : getSegmentSelectedSeats(segment_id).length < getPassengersCount();
  return can_add_seat;
}

export const changeFloor = (floor: number) => {
  valtio_store.active_floor = floor;
};

export const toggleSeatSelection = (
  seat: SeatCell,
  segment_id: string
): boolean => {
  if (isSeatSelected(seat.id, segment_id)) {
    return removeSeat(seat.id, segment_id);
  } else {
    return addSeat(seat, segment_id);
  }
};

export function getSelectedSeatsSegmentIds() {
  return valtio_store.selected_seats.map(s => s.segment_id);
}
export function shouldResetSelectedSeats(segment_id: string) {
  return !isFirstSegment(segment_id) &&
    isSegmentSeatSelectionOverbooking(segment_id)
    ? segment_id
    : null;
}

export function resetSegmentSelectedSeats(segment_id: string | null) {
  if (!segment_id) {
    return;
  }

  const find_idx = valtio_store.selected_seats.findIndex(
    seats => seats.segment_id === segment_id
  );

  valtio_store.selected_seats = valtio_store.selected_seats.filter(
    (_, idx) => idx !== find_idx
  );
}

export function setSeatSelectionFirstError(params: SeatSelectionFirstError) {
  valtio_store.error = params;
}

export function removePreviousSegmentSeats(segment_ids?: string[]) {
  if (!segment_ids) {
    valtio_store.selected_seats = [];
    return;
  }

  const current_segment_ids = getSelectedSeatsSegmentIds();
  const segments_to_remove = current_segment_ids.filter(
    id => !segment_ids.includes(id)
  );
  segments_to_remove.forEach(segment_id =>
    resetSegmentSelectedSeats(segment_id)
  );
}

const initial_obj: SeatSelectionFirstProxy = {
  active_floor: 0,
  selected_seats: [],
  error: null
};

export const valtio_store = proxy<SeatSelectionFirstProxy>(initial_obj);
